import { AfterViewInit, Directive, ElementRef, EventEmitter, forwardRef, Inject, Injectable, InjectionToken, Injector, Input, NgZone, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { map } from 'rxjs/operators';
import { DashboardService } from '../services/dashboard.service';
// import { CspService } from '../services/csp.service'; // Disabled - CSP now handled by .htaccess

declare const grecaptcha: any;

declare global {
  interface Window {
    grecaptcha: any;
    reCaptchaLoad: () => void;
    reCaptchaExecute: () => void;
    reCaptchaReset: () => void;
  }
}

export interface ReCaptchaConfig {
  badge?: 'bottomRight' | 'bottomLeft' | 'inline';
  type?: 'audio' | 'image';
  size?: 'invisible';
  tabindex?: number;
}

@Directive({
  selector: '[lgRecaptcha]',
  exportAs: 'lgRecaptcha'
})
export class ReCaptchaDirective implements OnInit {
  @Input() key: string;
  @Input() config: ReCaptchaConfig = {};
  @Input() lang: string;
  @Input() submit: () => void;

  @Output() captchaResponse = new EventEmitter<string>();
  @Output() captchaExpired = new EventEmitter();

  private widgetId: number;

  constructor(
    private element: ElementRef, 
    private ngZone: NgZone, 
    private dashboardService: DashboardService
    // Removed CSP service - not needed with .htaccess approach
  ) {
  }

  ngOnInit() {
    this.registerReCaptchaCallback();
    this.addScript();
  }

  registerReCaptchaCallback() {
    window.reCaptchaLoad = () => {
      const config = {
        ...this.config,
        'badge': 'bottomright',
        'size': 'invisible',
        'sitekey': this.key,
        'callback': this.onSuccess.bind(this),
        'expired-callback': this.onExpired.bind(this)
      };
      this.widgetId = this.render(this.element.nativeElement, config);
    };
    window.reCaptchaExecute = () => {
      this.execute();
    };
    window.reCaptchaReset = () => {
      this.reset();
    };
  }

  /**
   * Useful for multiple captcha
   * @returns {number}
   */
  getId() {
    return this.widgetId;
  }

  /**
   * onExpired
   */
  onExpired() {
    this.ngZone.run(() => {
      this.captchaExpired.emit();
    });
  }

  /**
   *
   * @param response
   */
  onSuccess(token: string) {
    this.ngZone.run(() => {
      this.verifyToken(token);
      this.captchaResponse.next(token);
    });
  }

  /**
   * Renders the container as a reCAPTCHA widget and returns the ID of the newly created widget.
   * @param element
   * @param config
   * @returns {number}
   */
  private render(element: HTMLElement, config): number {
    return grecaptcha.render(element, config);
  }

  /**
   * Resets the reCAPTCHA widget.
   */
  reset(): void {
    grecaptcha.reset(this.widgetId);
  }

  /**
   * Gets the response for the reCAPTCHA widget.
   * @returns {string}
   */
  getResponse(): string {
    console.log('test');
    return grecaptcha.getResponse(this.widgetId);
  }

  /**
   * Execute challenge for the reCAPTCHA widget.
   */
  execute(): void {
    grecaptcha.execute(this.widgetId);
  }

  /**
   * Add the script
   */
  addScript() {
    const lang = this.lang ? '&hl=' + this.lang : '';
    const src = `https://www.google.com/recaptcha/api.js?onload=reCaptchaLoad&render=explicit${lang}`;
    
    // Simple script creation without nonces - CSP handled by .htaccess
    let script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }

  /**
   * Verify Token
   */
  verifyToken(token: string) {
    this.dashboardService.validateToken({ token }).subscribe(res => {
      if (!res.success) {
        return { tokenInvalid: true }
      }
      this.submit();
    })
  }

}